import React, { useState } from 'react'
import styled from 'styled-components'
import { TARGETING } from './const'
import RdnaButton from '@ringdna/common/src/components/RdnaButton'
import RdnaText from '@ringdna/common/src/components/RdnaText'
import RdnaSelect from '@ringdna/common/src/components/RdnaFormElements/RdnaSelect'
import RdnaApiAutoComplete from '@ringdna/common/src/components/RdnaFormElements/RdnaApiAutoComplete'
import { RdnaConnectedForm } from '@ringdna/common/src/components/RdnaFormElements'
import { getRules } from '@ringdna/common/src/components/RdnaFormElements/_helpers'
import {
  AnnouncementTargetingType,
  AutoCompleteOptionData,
  ComplianceAnnouncement,
  PicklistItem,
  TargetingObject
} from '../../types'

const TargetingOptions = [
  { label: 'None', value: null },
  {
    label: 'All Users',
    value: AnnouncementTargetingType.ALL_USERS
  },
  {
    label: 'Team(s)',
    value: AnnouncementTargetingType.TEAMS
  },
  {
    label: 'Users with a Specific Salesforce Profile',
    value: AnnouncementTargetingType.SALESFORCE_PROFILES
  },
  {
    label: 'Users with a Specific Salesforce Role',
    value: AnnouncementTargetingType.SALESFORCE_ROLES
  },
  {
    label: 'Individual Users',
    value: AnnouncementTargetingType.INDIVIDUAL_USERS
  }
]

type Props = {
  excludeOpen: boolean
  accountId: number
  isSupervisorAgent: boolean
}

export default function Targeting({ excludeOpen, accountId, isSupervisorAgent }: Props) {
  const [showAdvanced, setShowAdvanced] = useState(excludeOpen)
  const disable = false

  let filteredOptions: { label: string; value: string | null }[] = TargetingOptions
  if (isSupervisorAgent) {
    filteredOptions = TargetingOptions.filter(
      option =>
        option.value === AnnouncementTargetingType.TEAMS || option.value === AnnouncementTargetingType.INDIVIDUAL_USERS
    )
  }

  return (
    <RdnaConnectedForm<ComplianceAnnouncement> data-testid={'announcement-targeting-section'}>
      {({ setValue, watch }, { RdnaFormController, getMuiProps }) => (
        <>
          <RdnaFormController
            name="targetingType"
            render={props => (
              <RdnaSelect
                name="targetingType"
                label={TARGETING.LABEL}
                placeholder={TARGETING.PLACEHOLDER}
                options={filteredOptions}
                {...getMuiProps(props)}
                value={getMuiProps(props).value || ''}
                disabled={disable}
                onChange={(e: any) => {
                  setValue('targetingType', e.target.value !== '' ? e.target.value : null, { shouldDirty: true })
                  setValue('targetObjects', [])
                  if (e.target.value === AnnouncementTargetingType.INDIVIDUAL_USERS) {
                    setValue('excludeUsers', [])
                  }
                }}
              />
            )}
          />
          {watch('targetingType') === AnnouncementTargetingType.INDIVIDUAL_USERS ? (
            <RdnaFormController
              name="targetObjects"
              rules={getRules(['required'])}
              render={props => (
                <div key={getMuiProps(props).name}>
                  <RdnaApiAutoComplete
                    data-testid={'announcement-individual-users-auto-complete'}
                    multiple
                    label={TARGETING.USERS.LABEL}
                    searchUrl={'/api/v2/app/users/picklist'}
                    searchQuery={{ accountId }}
                    placeholder={TARGETING.USERS.PLACEHOLDER}
                    {...getMuiProps(props)}
                    value={getMuiProps(props).value.length ? getMuiProps(props).value : null}
                    onChange={(e: any) => {
                      const mappedValue = e.map((item: { value: string }) => {
                        return { id: parseInt(item.value) }
                      })
                      setValue('targetObjects', mappedValue, { shouldDirty: true })
                    }}
                    isOptionEqualToValue={(option: AutoCompleteOptionData, value: { id: number }[]) => {
                      return value.some(item => {
                        return option.value === item.id
                      })
                    }}
                    defaultValue={
                      getMuiProps(props).value.map((item: PicklistItem) => ({
                        value: item.id.toString(),
                        text: item.name || item.displayName || ''
                      })) || []
                    }
                  />
                </div>
              )}
            />
          ) : (
            <RdnaFormController
              name="targetObjects"
              render={props => <input type={'hidden'} data-testid={'hidden-users'} value={getMuiProps(props).value} />}
            />
          )}
          {watch('targetingType') === AnnouncementTargetingType.TEAMS ? (
            <RdnaFormController
              name="targetObjects"
              rules={getRules(['required'])}
              render={props => (
                <div key={getMuiProps(props).name}>
                  <RdnaApiAutoComplete
                    data-testid={'announcement-teams-auto-complete'}
                    multiple
                    label={TARGETING.TEAMS.LABEL}
                    searchUrl={'/api/v2/app/teams/picklist'}
                    searchQuery={{ accountId }}
                    placeholder={TARGETING.TEAMS.PLACEHOLDER}
                    {...getMuiProps(props)}
                    value={getMuiProps(props).value.length ? getMuiProps(props).value : null}
                    onChange={(e: any) => {
                      const mappedValue = e.map((item: { value: string }) => {
                        return { id: parseInt(item.value) }
                      })
                      setValue('targetObjects', mappedValue, { shouldDirty: true })
                    }}
                    isOptionEqualToValue={(option: AutoCompleteOptionData, value: { id: number }[]) => {
                      return value.some(item => {
                        return option.value === item.id
                      })
                    }}
                    defaultValue={
                      getMuiProps(props).value.map((item: PicklistItem) => ({
                        value: item.id.toString(),
                        text: item.name || ''
                      })) || []
                    }
                  />
                </div>
              )}
            />
          ) : (
            <RdnaFormController
              name="targetObjects"
              render={props => <input type={'hidden'} data-testid={'hidden-teams'} value={getMuiProps(props).value} />}
            />
          )}
          {watch('targetingType') === AnnouncementTargetingType.SALESFORCE_ROLES ? (
            <RdnaFormController
              name="targetObjects"
              rules={getRules(['required'])}
              render={props => (
                <div key={getMuiProps(props).name}>
                  <RdnaApiAutoComplete
                    data-testid={'announcement-roles-auto-complete'}
                    multiple
                    queryName={'search'}
                    label={TARGETING.SF_ROLES.LABEL}
                    searchUrl={'/api/v3/users/salesforce/roles/picklist'}
                    searchQuery={{ accountId }}
                    placeholder={TARGETING.SF_ROLES.PLACEHOLDER}
                    {...getMuiProps(props)}
                    value={getMuiProps(props).value.length ? getMuiProps(props).value : null}
                    onChange={(e: any) => {
                      const mappedValue = e.map((item: { text: string }) => ({
                        name: item.text
                      }))
                      setValue('targetObjects', mappedValue, { shouldDirty: true })
                    }}
                    isOptionEqualToValue={(option: { value: string }, value: { name: string }[]) => {
                      return value.some(item => {
                        return option.value === item.name
                      })
                    }}
                    defaultValue={
                      getMuiProps(props).value.map((item: PicklistItem) => ({
                        text: item.name,
                        value: item.name
                      })) || []
                    }
                  />
                </div>
              )}
            />
          ) : (
            <RdnaFormController
              name="targetObjects"
              render={props => (
                <input type={'hidden'} data-testid={'hidden-salesforce-roles'} value={getMuiProps(props).value} />
              )}
            />
          )}
          {watch('targetingType') === AnnouncementTargetingType.SALESFORCE_PROFILES ? (
            <RdnaFormController
              name="targetObjects"
              rules={getRules(['required'])}
              render={props => (
                <div key={getMuiProps(props).name}>
                  <RdnaApiAutoComplete
                    data-testid={'announcement-profiles-auto-complete'}
                    multiple
                    queryName={'search'}
                    label={TARGETING.SF_PROFILES.LABEL}
                    searchUrl={'/api/v3/users/salesforce/profiles/picklist'}
                    searchQuery={{ accountId }}
                    placeholder={TARGETING.SF_PROFILES.PLACEHOLDER}
                    {...getMuiProps(props)}
                    value={getMuiProps(props).value.length ? getMuiProps(props).value : null}
                    onChange={(e: any) => {
                      const mappedValue = e.map((item: { value: string }) => {
                        return {
                          id: parseInt(item.value)
                        }
                      })
                      setValue('targetObjects', mappedValue, { shouldDirty: true })
                    }}
                    isOptionEqualToValue={(option: AutoCompleteOptionData, value: { id: number }[]) => {
                      return value.some(item => {
                        return option.value === item.id
                      })
                    }}
                    defaultValue={
                      getMuiProps(props).value.map((item: PicklistItem) => ({
                        text: item.name || '',
                        value: item.id || ''
                      })) || []
                    }
                  />
                </div>
              )}
            />
          ) : (
            <RdnaFormController
              name="targetObjects"
              render={props => (
                <input type={'hidden'} data-testid={'hidden-salesforce-profiles'} value={getMuiProps(props).value} />
              )}
            />
          )}
          {watch('targetingType') && watch('targetingType') !== AnnouncementTargetingType.INDIVIDUAL_USERS && (
            <>
              <Excludes $show={showAdvanced}>
                <RdnaText variant={'meta'}>{TARGETING.EXClUDE_USERS.TITLE}</RdnaText>
                <RdnaFormController
                  name="excludeUsers"
                  rules={getRules(['required'])}
                  render={props => (
                    <div key={getMuiProps(props).name}>
                      <RdnaApiAutoComplete
                        multiple
                        data-testid={`exclude-users`}
                        searchUrl={'/api/v2/app/users/picklist'}
                        searchQuery={{ accountId }}
                        placeholder={TARGETING.EXClUDE_USERS.PLACEHOLDER}
                        {...getMuiProps(props)}
                        value={getMuiProps(props).value.length ? getMuiProps(props).value : null}
                        onChange={(e: any) => {
                          const mappedValue = e.map((item: { value: string }) => {
                            return { id: parseInt(item.value) }
                          })
                          setValue('excludeUsers', mappedValue, { shouldDirty: true })
                        }}
                        isOptionEqualToValue={(option: AutoCompleteOptionData, value: { id: number }[]) => {
                          return value.some(item => {
                            return option.value === item.id
                          })
                        }}
                        defaultValue={
                          getMuiProps(props).value.map((item: TargetingObject) => ({
                            value: item.id.toString(),
                            text: item.name || item.displayName || ''
                          })) || []
                        }
                      />
                    </div>
                  )}
                />
              </Excludes>
              <RdnaButton
                className={disable ? 'hidden' : ''}
                style={{ padding: 0 }}
                disabled={disable}
                text={`${showAdvanced ? TARGETING.ADVANCED.HIDE : TARGETING.ADVANCED.SHOW}`}
                color={'neutral'}
                variant={'text'}
                type={'button'}
                onClick={() => setShowAdvanced(!showAdvanced)}
              />
            </>
          )}
        </>
      )}
    </RdnaConnectedForm>
  )
}

const Excludes = styled.div<{ $show: boolean }>`
  display: ${props => (props.$show ? 'flex' : 'none')};
  align-items: flex-start;
  width: 100%;
  > span {
    margin-right: ${props => props.theme.spacing * 2}px;
    margin-top: ${props => props.theme.spacing * 3}px;
  }
  > div {
    width: 100%;
  }
`
